import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useChurch } from "@/hooks/useChurch";
import { useFreemium } from "@/hooks/useFreemium";
import { X } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

type Family = Tables<"families">;
type Member = Tables<"members">;

interface Props { open: boolean; onClose: () => void; family: Family | null; assignedMemberIds?: string[]; }

export default function FamilyFormDialog({ open, onClose, family, assignedMemberIds = [] }: Props) {
  const queryClient = useQueryClient();
  const { profile } = useChurch();
  const { requireSubscription } = useFreemium();
  const isEdit = !!family;

  const [form, setForm] = useState({ family_name: "", address: "", city: "", state: "", zip_code: "", phone: "", notes: "" });
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);

  const { data: allMembers = [] } = useQuery({
    queryKey: ["members", profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];
      const { data, error } = await supabase.from("members").select("*").eq("church_id", profile.id).order("full_name");
      if (error) throw error;
      return data as Member[];
    },
    enabled: !!profile?.id,
  });

  useEffect(() => {
    if (!open) return; // only initialize when dialog is opening
    if (family) {
      setForm({
        family_name: family.family_name,
        address: family.address || "",
        city: (family as any).city || "",
        state: (family as any).state || "",
        zip_code: (family as any).zip_code || "",
        phone: family.phone || "",
        notes: family.notes || "",
      });
      setSelectedMembers(assignedMemberIds);
    } else {
      setForm({ family_name: "", address: "", city: "", state: "", zip_code: "", phone: "", notes: "" });
      setSelectedMembers([]);
    }
  // assignedMemberIds intentionally omitted: it's a new array ref on every parent render
  // and including it causes the form to reset on background React Query refetches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, family?.id]);

  const filteredMembers = useMemo(() => {
    const term = memberSearch.trim().toLowerCase();
    if (!term) return allMembers.filter(m => !selectedMembers.includes(m.id)).slice(0, 8) as Member[];
    return allMembers
      .filter((member) => (member.full_name || "").toLowerCase().includes(term))
      .slice(0, 12);
  }, [allMembers, memberSearch, selectedMembers]);

  const selectedMemberRows = useMemo(
    () => selectedMembers
      .map((memberId) => allMembers.find((member) => member.id === memberId))
      .filter(Boolean) as Member[],
    [selectedMembers, allMembers]
  );

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        family_name: form.family_name.trim(),
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zip_code: form.zip_code.trim() || null,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
      };
      let familyId: string;
      if (isEdit) {
        const { error } = await supabase.from("families").update(payload).eq("id", family.id);
        if (error) throw error;
        familyId = family.id;
      } else {
        payload.church_id = profile?.id;
        const { data, error } = await supabase.from("families").insert(payload).select("id").single();
        if (error) throw error;
        familyId = data.id;
      }
      if (isEdit) await supabase.from("family_members").delete().eq("family_id", familyId);
      if (selectedMembers.length > 0) {
        const rows = selectedMembers.map((member_id) => ({ family_id: familyId, member_id }));
        const { error } = await supabase.from("family_members").insert(rows);
        if (error) throw error;
      }
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["families"] });  onClose(); },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!requireSubscription()) return;
    if (!form.family_name.trim()) { toast.error("Family name is required"); return; }
    mutation.mutate();
  };
  const toggleMember = (id: string) => setSelectedMembers((prev) => prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]);
  const toMemberAddress = (member: Member) => {
    const parts = [(member as any).address, (member as any).city, (member as any).state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  const getMemberInitials = (name: string | null) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-heading">{isEdit ? "Edit Family" : "Add New Family"}</SheetTitle></SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2"><Label>Family Name *</Label><Input value={form.family_name} onChange={(e) => setForm((p) => ({ ...p, family_name: e.target.value }))} required /></div>
          <div className="space-y-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))} /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 [&>:first-child]:col-span-2 sm:[&>:first-child]:col-span-1">
            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={form.city}
                onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                placeholder="City"
                autoComplete="address-level2"
              />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Select value={form.state} onValueChange={(v) => setForm((p) => ({ ...p, state: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select state…" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>ZIP Code</Label><Input value={form.zip_code} onChange={(e) => setForm((p) => ({ ...p, zip_code: e.target.value }))} /></div>
          </div>
          <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} /></div>
          <div className="space-y-2">
            <Label>Family Members</Label>
            <div className="relative">
              <Input
                value={memberSearch}
                onChange={(e) => { setMemberSearch(e.target.value); setMemberPickerOpen(true); }}
                onFocus={() => setMemberPickerOpen(true)}
                onBlur={() => setTimeout(() => setMemberPickerOpen(false), 120)}
                placeholder="Search member names..."
              />
              {memberPickerOpen && (filteredMembers.length > 0 || memberSearch.trim().length > 0) && (
                <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-popover border border-border rounded-lg shadow-md max-h-56 overflow-y-auto">
                  {filteredMembers.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-3 py-2">No matching members</p>
                  ) : (
                    filteredMembers.map((member) => {
                      const isSelected = selectedMembers.includes(member.id);
                      const addr = toMemberAddress(member);
                      return (
                        <button
                          type="button"
                          key={member.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            toggleMember(member.id);
                            setMemberSearch("");
                            setMemberPickerOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-accent/10 transition-colors flex items-center gap-2"
                        >
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary text-xs font-semibold shrink-0"
                          >
                            {getMemberInitials(member.full_name)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate">{member.full_name}</p>
                            {addr && <p className="text-xs text-muted-foreground truncate">{addr}</p>}
                          </div>
                          {isSelected && <span className="text-xs text-primary font-semibold">✓</span>}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {selectedMemberRows.length > 0 && (
              <div className="border border-border rounded-lg p-2 space-y-1.5">
                {selectedMemberRows.map((member) => (
                  <div key={member.id} className="flex items-center gap-2 rounded-md bg-secondary/50 px-2.5 py-2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary text-xs font-semibold shrink-0"
                    >
                      {getMemberInitials(member.full_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{member.full_name}</p>
                      {toMemberAddress(member) && <p className="text-xs text-muted-foreground truncate">{toMemberAddress(member)}</p>}
                    </div>
                    <button type="button" onClick={() => toggleMember(member.id)} className="text-muted-foreground hover:text-foreground transition-colors mt-0.5">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">{selectedMembers.length} member(s) selected</p>
          </div>
          <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={2} /></div>
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-2">
            <Button type="button" variant="outline" className="w-full sm:w-auto min-h-[44px]" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="w-full sm:w-auto min-h-[44px]" disabled={mutation.isPending}>{mutation.isPending ? "Saving..." : isEdit ? "Update" : "Add Family"}</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
